import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ORDERS_TYPES } from "../../../../../../../redux/constants/OrdersPage";
import { useTypedSelector } from "../../../../../../../redux/store";
import {
	filterOrdersByPageFilters,
	hasActiveOrderPageFilters,
} from "../../../../../../../components/OrderPageWidgets/OrderTable/filterOrdersByPageFilters";

type Tab = (typeof ORDERS_TYPES)[keyof typeof ORDERS_TYPES];

/**
 * Счётчики вкладок /orders с учётом клиентских фильтров (поиск
 * исполнителя/транспорта/заказа, компании-филиалы-классы) — теми же, что
 * отбирают строки таблицы. Без активного фильтра берётся серверный счётчик.
 */
export default function useOrdersTabCounts(): Record<Tab, number> {
	const { t } = useTranslation();
	const orders = useTypedSelector((state) => state.ordersPageReducer.orders);
	const filters = useTypedSelector(
		(state) => state.ordersPageReducer.filters,
	);

	return useMemo(() => {
		const pointOnMap = t(["point_on_map"]);

		const serverCountOf = (tab: Tab) =>
			orders[tab]?.pagination?.count ?? orders[tab]?.count ?? 0;

		const countOf = (tab: Tab) => {
			if (!hasActiveOrderPageFilters(filters[tab])) {
				return serverCountOf(tab);
			}
			return filterOrdersByPageFilters(
				orders[tab]?.items ?? [],
				filters[tab],
				pointOnMap,
			).length;
		};

		return {
			[ORDERS_TYPES.LIVE]: countOf(ORDERS_TYPES.LIVE),
			[ORDERS_TYPES.PRELIMINARY]: countOf(ORDERS_TYPES.PRELIMINARY),
			[ORDERS_TYPES.EXECUTABLE]: countOf(ORDERS_TYPES.EXECUTABLE),
			// Без фильтра «Усі» — сумма открытых вкладок, как в
			// selectOrdersTabCounts (серверный счётчик «all» может дрейфовать).
			[ORDERS_TYPES.ALL]: hasActiveOrderPageFilters(
				filters[ORDERS_TYPES.ALL],
			)
				? countOf(ORDERS_TYPES.ALL)
				: serverCountOf(ORDERS_TYPES.LIVE) +
				  serverCountOf(ORDERS_TYPES.PRELIMINARY) +
				  serverCountOf(ORDERS_TYPES.EXECUTABLE),
			[ORDERS_TYPES.CLOSED]: serverCountOf(ORDERS_TYPES.CLOSED),
		} as Record<Tab, number>;
	}, [filters, orders, t]);
}
